<template>
  <pl-dialog
    :title="`Which teachers accounts do you want to import to Parlay?`"
    :size="'xl'"
    @close="$emit('close')"
    :disableOutsideClick="true"
  >
    <body class="px-7 pt-7 flex-grow overflow-y-auto">
      <div class="sub-header">
        After Signing in to Parlay, teachers will be able to roster students
        from their LMS / SIS directly to their Parlay classroom.
      </div>
      <div v-if="teachers" class="mt-4">
        <div
          v-if="loading_data"
          class="flex items-center justify-center h-full"
        >
          <pl-spinner class="x2" />
        </div>
        <div v-else>
          <integrations-teachers-filters-bar
            class="mb-4"
            :teachersFilters="teachersFilters"
            @filters="filters"
            @admin-portal-filter-changed="resetFilters"
          />
          <table v-if="teachers.length" class="teachers-table w-full h-full">
            <thead>
              <tr class="row no-margin middle-xs">
                <td class="col-xs-1" style="padding-left: 10px">
                  <pl-checkbox
                    v-model="allSelected"
                    @change="handleSelectAll($event)"
                    class="col-xs-1"
                  />
                </td>
                <td class="col-xs-2">First Name</td>
                <td class="col-xs-2">Last Name</td>
                <td class="col-xs-3">Email</td>
                <td class="col-xs-1">Grade</td>
                <td class="col-xs-2">School Site</td>
                <td class="col-xs-1">Existing Parlay Account ?</td>
              </tr>
            </thead>
            <tbody
              id="integrations-teacher-modal"
              @scroll="$emit('scroll-on-table')"
            >
              <tr
                class="row no-margin"
                v-for="(t, index) in teachers"
                :key="index"
              >
                <td class="col-xs-1">
                  <pl-checkbox
                    v-model="selectedTeachers[index]"
                    @change="handleSelectTeacher($event, index)"
                    class="col-xs-1"
                  />
                </td>
                <td :title="t.first_name" class="col-xs-2 truncate">
                  {{ t.first_name }}
                </td>
                <td :title="t.last_name" class="col-xs-2 truncate">
                  {{ t.last_name }}
                </td>
                <td class="col-xs-3">
                  <div v-if="t.email" :title="t.email" class="truncate">
                    {{ t.email }}
                  </div>
                  <div v-else>-</div>
                </td>
                <td class="col-xs-1">
                  <div v-if="t.grades && t.grades.length">
                    <div
                      v-for="(grade, index) in t.grades"
                      :key="index"
                      :title="grade"
                      class="truncate"
                    >
                      {{ grade }}
                    </div>
                  </div>
                  <div v-else>-</div>
                </td>
                <td class="col-xs-2">
                  <div
                    v-if="t.school_site"
                    :title="t.school_site"
                    class="truncate"
                  >
                    {{ t.school_site }}
                  </div>
                  <div v-else>-</div>
                </td>
                <td class="col-xs-1 flex justify-center">
                  <div v-if="t.parlay_account" class="text-green-600">
                    <i class="fa-solid fa-check text-base leading-4" />
                  </div>
                  <div v-else class="text-red-600">
                    <i
                      class="fa fa-times text-base leading-4"
                      aria-hidden="true"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="row middle-xs center-xs no-teachers-text ph5">
            Parlay has not received any user information from this integration
            yet. If you recently enabled the integration, it can take up to 24
            hours for the data to propagate.
          </div>
        </div>
      </div>
    </body>
    <footer class="mb-7 mt-4 flex justify-center items-center">
      <pl-spinner v-if="loading" style="margin: auto" class="x2" />
      <button
        v-else
        class="px-8 py-2 bg-primary rounded cursor-pointer text-white font-semibold disabled:opacity-50"
        @click="importTeachers"
        :disabled="(teachers && !teachers.length) || !selected_teachers.length"
      >
        Import teachers to Parlay
      </button>
    </footer>
  </pl-dialog>
</template>
<script>
  import { EventBus } from 'utils/event-bus';
  import integrationsTeachersFiltersBar from './integrationsTeachersFiltersBar.vue';
  import adminServices from 'services/admin/adminServices';
  import { mapGetters } from 'vuex';

  export default {
    components: {
      integrationsTeachersFiltersBar,
    },
    watch: {
      teachers() {
        const is_selected = this.selectedTeachers.filter((s) => s === true);
        this.allSelected = is_selected.length === this.teachers.length;
      },
    },
    data() {
      return {
        allSelected: false,
        teachersFilters: {},
        loading: false,
        selectedTeachers: [],
      };
    },
    props: {
      teachers: {
        type: Array,
        default() {
          return [];
        },
      },
      integration_id: {
        type: String,
        default() {
          return null;
        },
      },
      integration_name: {
        type: String,
        default() {
          return '';
        },
      },
      loading_data: {
        type: Boolean,
        default() {
          return false;
        },
      },
    },
    computed: {
      ...mapGetters(['getCurrentSchool']),
      selected_teachers() {
        return this.selectedTeachers.filter((s) => s === true);
      },
      validMembership() {
        return (
          new Date(
            this.getCurrentSchool.membership[0].school_membership.end_date
          ) > new Date()
        );
      },
    },
    methods: {
      handleSelectAll(status) {
        this.selectedTeachers = this.teachers.map(() => status);
        const is_selected = this.selectedTeachers.filter((s) => s === true);
        this.allSelected = is_selected.length === this.teachers.length;
      },
      handleSelectTeacher(event, index) {
        let boolean_status = false;
        if (event === 'on' || event == true) {
          boolean_status = true;
        } else {
          boolean_status = false;
        }
        if (typeof index === 'number') {
          const status = [...this.selectedTeachers];
          status[index] = boolean_status;
          this.selectedTeachers = status;
          this.allSelected =
            this.selected_teachers.length === this.teachers.length;
        }
      },
      resetFilters() {
        this.$emit('admin-portal-filter-changed');
      },
      filters(value) {
        this.$emit('filters', value);
      },
      sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
      },
      async importTeachers() {
        const schoolID = this.$route.params.schoolID;
        const integrationID = this.integration_id;
        const teachers_to_import = this.teachers.filter(
          (s, index) => this.selectedTeachers[index]
        );
        const payload = {
          teachers: teachers_to_import,
        };
        let n_teachers = 0;
        teachers_to_import.map((x) => {
          x.email ? (n_teachers = n_teachers + 1) : null;
        });
        if (!this.getCurrentSchool.membership.length) {
          const toast = {
            type: 'Error',
            message: `Your school has no membership.`,
            time: 5000,
          };
          EventBus.$emit('createToast', toast);
          return;
        }

        const { number_of_licenses, licenses_used, membership_type } =
          this.getCurrentSchool.membership[0].school_membership;
        let available_licenses = number_of_licenses - licenses_used;
        if (n_teachers > 0 && !this.validMembership) {
          const toast = {
            type: 'Error',
            message: `Your school membership has expired.`,
            time: 5000,
          };
          EventBus.$emit('createToast', toast);
          return;
        }

        if (membership_type === 'teacher') {
          if (available_licenses < n_teachers) {
            let teachers_emails = teachers_to_import.map((x) => x.email);
            const {
              data: { school_used_licenses },
            } = await adminServices.getTeachersWithLicense(
              this.getCurrentSchool.id,
              { teachers_emails }
            );
            const new_teacher_licenses = n_teachers - school_used_licenses;
            if (available_licenses < new_teacher_licenses) {
              const toast = {
                type: 'Error',
                message: `You have ${available_licenses} available licenses.`,
                time: 5000,
              };
              EventBus.$emit('createToast', toast);
              return;
            }
          }
        }

        const res =
          await adminServices.rosterEdlinkSchoolIntegrationTeachersJob(
            schoolID,
            integrationID,
            payload
          );
        if (res.data.id) {
          this.loading = true;
          let state = '';
          do {
            await this.sleep(1000);
            const response =
              await adminServices.getRosterEdlinkSchoolIntegrationTeachersJobStatus(
                schoolID,
                integrationID,
                res.data.id
              );
            state = response.data.state;
          } while (state !== 'completed' && state !== 'failed');
          if (state === 'completed') {
            this.loading = false;
            const toast = {
              type: 'Success',
              message: 'Teachers successfully imported!',
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
            this.$store.dispatch('getSchoolDetails', schoolID);
            this.$emit('close');
          } else if (state === 'failed') {
            this.loading = false;
            const toast = {
              type: 'Error',
              message:
                'An error has occurred while importing courses from edLink.',
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
          }
        }
      },
    },
    mounted() {},
  };
</script>
<style lang="stylus" scoped>
  @import '~app/global-stylus.styl';
  .header
    font-size: 24px
    line-height: 29px
    color: #273248
  .sub-header
    font-size: 18px
    line-height: 22px
    color: #273248
  .no-teachers-text
    height: calc(85vh - 260px)
    font-size: 24px
    line-height: 29px
    color: #595959
  .teachers-table
    tbody
      display: block
      overflow-y: auto
      overflow-x: hidden
      @media (max-width: 1279px) and (min-width: 500px)
        height: 600px
      @media (min-width: 1280px)
        height: 450px
      tr
        border: 2px solid #d9d9d9
        border-bottom: none
        align-items: center
        &:last-child
          border-bottom: 2px solid #d9d9d9
      td
        padding: 16px 8px
        font-weight: 500
  .footer
    border-top: 1px solid #979797
</style>



// WEBPACK FOOTER //
// integrationsTeachersModal.vue?17f3cc16